import React from 'react';
import ReactDOM from 'react-dom';
import EmbedContainer from 'react-oembed-container';
import fetchOembed from '@surfline/web-common/dist/utils/fetchOembed';
import { doExternalFetch } from '@surfline/web-common/dist/utils/api/baseFetch';
import { GoogleDFP, AddToCalendar, CamPlayer, PremiumCamCTA } from '@surfline/quiver-react';
import { fetchReport } from '../common/api/spot';
import PremiumVideoCTA from '../components/PremiumVideoCTA';
import loadAdConfig from './adConfig';
import { fetchCamEmbedDetails } from '../common/api/cameras';
import config from '../config';

const renderAd = (element, adNumber, adIdentifier, dfpKeyword, qaFlag) => {
  element.insertAdjacentHTML('afterend', `<div id="ad-${adNumber}" />`);
  ReactDOM.render(
    <GoogleDFP
      adConfig={loadAdConfig(adIdentifier, [['qaFlag', qaFlag], ['contentKeyword', dfpKeyword]])}
    />,
    document.getElementById(`ad-${adNumber}`),
  );
};

export const renderArticleAds = (articleRef, children, dfpKeyword, createdAt, qaFlag) => {
  const adTracker = { count: 0, placed: 0 };
  children.forEach((node, index) => {
    if (
      node.tagName === 'P' ||
      node.tagName === 'IMG' ||
      node.className.indexOf('video') > -1 ||
      node.className.indexOf('img') > -1
    ) {
      adTracker.count += 1;
    }
    if (adTracker.count === 4 && adTracker.placed < 3) {
      const adNumber = adTracker.placed + 1;
      const adIdentifier =
        adNumber === 1 && new Date(createdAt) < new Date('2019-04-29 00:00:00')
          ? 'legacyEditContent'
          : `editBody${adNumber}`;
      renderAd(articleRef.current.children[index], adNumber, adIdentifier, dfpKeyword, qaFlag);
      adTracker.count = -1;
      adTracker.placed += 1;
    } else if (index === children.length - 1 && adTracker.placed === 0) {
      const adNumber = adTracker.placed + 1;
      renderAd(
        articleRef.current.children[index],
        adNumber,
        `editBody${adNumber}`,
        dfpKeyword,
        qaFlag,
      );
    }
  });
};

export const renderIgElements = () => {
  const igElements = document.querySelectorAll('[data-sl-ig]');
  if (igElements.length) {
    [...igElements].map(async (ig) => {
      const {
        oembedObj: { html },
      } = await fetchOembed(ig.dataset.slIg, '/travel/oembed');
      ReactDOM.render(
        <EmbedContainer markup={html}>
          <div dangerouslySetInnerHTML={{ __html: html }} />
        </EmbedContainer>,
        ig,
      );
    });
  }
};

export const renderTwitterElements = () => {
  const twitterElements = document.getElementsByClassName('twitter-tweet');
  if (twitterElements.length) {
    [...twitterElements].map(async (tweetElement) => {
      const tweetUrl = tweetElement.getElementsByTagName('a')[1].href;
      const {
        oembedObj: { html }
      } = await fetchOembed(tweetUrl, '/travel/oembed');
      ReactDOM.render(
        <EmbedContainer markup={html}>
          <div dangerouslySetInnerHTML={{ __html: html }} />
        </EmbedContainer>, tweetElement
      )
    })
  }
}

export const renderFeaturedVideo = (jwId) => {
  const featuredVideoElements = document.getElementsByClassName('sl-swell-event-featured-media');
  if (featuredVideoElements.length) {
    [...featuredVideoElements].map(async (jw) => {
      if (window && window.jwplayer && jwId) {
        window
          .jwplayer(jw.id)
          .setup({ playlist: `https://content.jwplatform.com/feeds/${jwId}.json`, ph: 2 });
      }
    });
  }
};

export const renderJWElements = () => {
  const jwElements = document.querySelectorAll("[id*='jw']");
  if (jwElements.length) {
    [...jwElements].map(async (jw) => {
      const jwId = jw.id.split('_')[1];
      if (window && window.jwplayer && jwId) {
        window
          .jwplayer(jw.id)
          .setup({ playlist: `https://content.jwplatform.com/feeds/${jwId}.json`, ph: 2 });
      }
    });
  }
};

export const renderVideoPaywalls = (
  onClickVideoPaywall,
  onClickSignIn,
  shouldPaywall,
  doLoadTreatment,
) => {
  const videoPaywallElement = document.getElementById('sl-premium-video-cta');
  const videoPaywallCaptionElements = document.getElementsByClassName('sl-premium-video-text');

  if (videoPaywallElement && shouldPaywall) {
    const paywallSplit = doLoadTreatment('sl_web_editorial_vid_paywall');
    if (paywallSplit === 'on') {
      ReactDOM.render(
        <PremiumVideoCTA
          onClickVideoPaywall={onClickVideoPaywall}
          imageUrl={videoPaywallElement.getAttribute('data-image-url')}
          onClickSignIn={onClickSignIn}
          videoText={
            videoPaywallCaptionElements.length ? videoPaywallCaptionElements[0].innerHTML : ''
          }
        />,
        videoPaywallElement,
      );
    }
  }
};

const createCameraObject = cameraDetails => ({
  streamUrl: cameraDetails.cam.streamUrl,
  stillUrl: cameraDetails.cam.stillUrl,
  status: {
    isDown: !!cameraDetails.cam.isDown.status,
    message: cameraDetails.cam.isDown.message,
    subMessage: cameraDetails.cam.isDown.subMessage,
  },
  isPrerecorded: false,
  isPremium: cameraDetails.cam.isPremium,
  title: 'title'
});

export const renderCameraEmbeds = (isPremium, articleId, articleTitle, category, doTrackEvent) => {
  const cameraEmbeds = document.getElementsByClassName('wp-cam-embed');
  if (cameraEmbeds.length) {
    [...cameraEmbeds].map(async (embedElement) => {
      const cameraId = embedElement.getAttribute('data-sl-cam-id');
      const detailsJson = await fetchCamEmbedDetails(cameraId);
      const spotReport = await fetchReport(detailsJson.spot.id);
      const camera = createCameraObject(detailsJson);

      const camStillUrl = camera.stillUrl;
      const isPremiumCam = camera.isPremium;
      const showPremiumCamPaywall = isPremiumCam && !isPremium;

      const segmentProperties = {
        ctaLocation: 'premium only cam - cam embed',
        category,
        title: articleTitle,
        contentId: articleId,
        spotId: detailsJson.spot.id,
        spotName: detailsJson.spot.name,
      };
      if (showPremiumCamPaywall) {
        ReactDOM.render(
          <div className="video-wrap-container">
            <div className="video-wrap">
              <PremiumCamCTA
                camStillUrl={camStillUrl}
                href={`${config.surflineHost}${config.funnelUrl}`}
                onClickPaywall={() => doTrackEvent('Clicked Subscribe CTA', segmentProperties)}
              />
            </div>
          </div>, embedElement);
      } else {
        ReactDOM.render(
          <div className="video-wrap-container">
            <div className="video-wrap">
              <CamPlayer
                aspectRatio="16:9"
                adTag={{ iu: '/1024858/SL_Editorial_Embed_PreRoll' }}
                advertisingIds={spotReport.associated.advertising}
                playerId={cameraId || 'sl-editorial-embed'}
                camera={camera}
                spotName={detailsJson.spot.name}
                spotId={detailsJson.spot.id}
                isPremium={isPremium}
                isMultiCam={false}
              />
            </div>
          </div>
          , embedElement);
      }
    });
  }
};

const onClickAddToCalendar = (doTrackEvent, trackData) => {
  doTrackEvent('Clicked Link', trackData);
}

export const renderCalendarEvents = (doTrackEvent, articleId) => {
  const eventElements = document.getElementsByClassName('cal-event');
  if (eventElements.length) {
    [...eventElements].map(async (event) => {
      try {
        const spotReport = await fetchReport(event.querySelector('#cal-event-spot').innerHTML);
        const {
          spot: { name: spotName },
          associated: { href: spotUrl },
        } = spotReport;
        const startTime = event.querySelector('#cal-event-start').innerHTML.toString();
        const endTime = event.querySelector('#cal-event-end').innerHTML.toString();
        const eventObj = {
          title: `Surf ${spotName}`,
          description: `Check out the forecast for ${spotName} at ${spotUrl}`,
          location: spotName,
          startTime,
          endTime,
        };
        const trackData = {
          articleId,
          category: 'editorial',
          linkLocation: 'editorialContent',
          linkName: 'Add to Calendar',
          linkURL: spotUrl
        };
        ReactDOM.render(
          <AddToCalendar
            onClickAdd={() => onClickAddToCalendar(doTrackEvent, trackData)}
            event={eventObj}
          />,
        event);
      } catch (err) {
        console.error(err);
      }
    });
  }
};

export const renderSurfNetworkVideo = async () => {
  const surfNetworkContainerElement = document.getElementById('sl-surfnetwork-video');
  if (surfNetworkContainerElement) {
    const videoId = surfNetworkContainerElement.getAttribute('data-sl-video-id');
    if (videoId) {
      const surfNetworkToken = await doExternalFetch('/travel/surfnetwork');
      ReactDOM.render(
        <div itemScope itemType="https://schema.org/VideoObject">
          <div style={{ position: 'relative', overflow: 'hidden', paddingBottom: '56.25%' }}>
            <iframe
              src={`https://api.nodplatform.com/partner/surfline/watch/${videoId}.html?access_token=${surfNetworkToken}`}
              width="100%"
              height="100%"
              frameBorder="0"
              scrolling="auto"
              title="SurfNetwork"
              style={{ position: 'absolute' }}
              allowFullScreen
            />
          </div>
        </div>,
        surfNetworkContainerElement,
      );
    }
  }
};

export const trackArticleLinkClicks = (articleId, doTrackEvent) => {
  const articleBody = document.getElementById('sl-editorial-article-body');
  const articleLinks = articleBody.querySelectorAll('a');
  if (articleLinks.length) {
    [...articleLinks].map(async (ig, index) => {
      articleLinks[index].addEventListener(
        'click',
        event =>
          doTrackEvent('Clicked Link', {
            articleId,
            category: 'editorial',
            linkLocation: 'editorialContent',
            linkName: event.target.innerText,
            linkURL: event.target.href,
          }),
        false,
      );
    });
  }
};

export const trackSwellEventClickLinks = (articleId, doTrackEvent) => {
  const articleBody = document.getElementById('sl-swell-event__content__feed');
  const articleLinks = articleBody.querySelectorAll('a');
  if (articleLinks.length) {
    [...articleLinks].map(async (ig, index) => {
      articleLinks[index].addEventListener(
        'click',
        event =>
          doTrackEvent('Clicked Link', {
            articleId,
            category: 'editorial',
            linkLocation: 'Swell Alert',
            linkName: event.target.innerText,
            linkURL: event.target.href,
          }),
        false,
      );
    });
  }
};
